import type { MetricRegistryEntry } from './metric-registry';

type DefinitionDigestedMetricRegistryEntry = MetricRegistryEntry &
  Readonly<{ definitionDigest: string }>;

export const ORG_ANALYSIS_OPTIMIZATION_METRIC_REGISTRY_SEED: readonly DefinitionDigestedMetricRegistryEntry[] =
  Object.freeze([
    Object.freeze({
      metricKey: 'fully_loaded_cost_per_fte_usd',
      registryVersion: 'metric_registry_v1',
      registryLayer: 'core',
      appliesTo: [{ dimension: 'business_line', members: 'all' as const }],
      label: 'Fully loaded cost per FTE',
      definition:
        'Annual fully loaded position cost per explicitly declared FTE-equivalent in the governed roster scope.',
      unit: 'USD per FTE',
      frame: 'annual_fully_loaded_cost_per_fte',
      direction: 'lower_is_better',
      blendPolicy: { kind: 'never_blend' },
      aggregationRules: [
        {
          dimension: 'business_line',
          rollup: 'reported_segment',
          ruleFormulaId: 'ratio_recompute_v1',
          materialization: 'computed_on_demand',
        },
        {
          dimension: 'business_line',
          rollup: 'enterprise',
          ruleFormulaId: 'ratio_recompute_v1',
          materialization: 'computed_on_demand',
        },
        {
          dimension: 'period',
          rollup: 'fiscal_year',
          ruleFormulaId: 'ratio_recompute_v1',
          materialization: 'computed_on_demand',
        },
        {
          dimension: 'period',
          rollup: 'quarter',
          ruleFormulaId: 'ratio_recompute_v1',
          materialization: 'computed_on_demand',
        },
      ],
      reconciliationRule:
        'Divide the governed position-cost rollup by explicit FTE-equivalence for the same roster scope and reporting cut.',
      requiredEvidenceClasses: [
        'position_cost_roster',
        'fully_loaded_cost_per_fte_model',
      ],
      benchmarkable: false,
      temporalType: 'ratio',
      sourceOfTruthClass: 'derived',
      ownerModuleId: 'org-analysis-optimization',
      // #5734 fix round (2026-08-31): declared while folding origin/main, whose
      // #5652 merge (fully-loaded-cost-per-fte) added this row after the
      // baseline was pinned. Definition is an unambiguous per-head labor cost
      // ratio (position cost / FTE-equivalent), so it takes the same shape as
      // the existing OPERATING_EXPENSE_PER_HEAD_MEASURE_IDENTITY pattern, but
      // with measuredDimension 'labor_cost' since this is a headcount cost
      // rate, not a below-the-line operating_expense line.
      // Round-3 CTO correction (2026-08-31): normalizationBasis is
      // 'fte_capacity', not 'reporting_headcount' — the governed formula
      // divides by the sum of declared fteEquivalent, not raw head count
      // (org-analysis-optimization.fully-loaded-cost-per-fte.spec.ts:44-45:
      // two positions totalling 1.5 FTE yield 200, not the 150 a
      // headcount denominator would produce). reporting_headcount is only
      // the modeled fallback path when no explicit FTE data is present.
      measureIdentity: {
        measuredDimension: 'labor_cost',
        valueForm: 'ratio',
        unitFamily: 'currency_per_person',
        normalizationBasis: 'fte_capacity',
      },
      sourceBindingKeys: ['fully_loaded_cost_per_fte_usd'],
      source:
        'apps/api/src/decision-modules/org-analysis-optimization/org-analysis-optimization.read-figures.ts#metricKey:fully_loaded_cost_per_fte_usd',
      definitionDigest:
        '404fdc1cc07d182532781a1e097d146309be88926a2f0ee9d3718a5e226a817f',
    } as const),
  ]);
